import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAgency } from '../../context/AgencyContext';

const ALLOWED_ROLES = ['super_admin', 'billing_manager'];

/**
 * State, data fetching, and derived rankings/totals for the Cross-Agency
 * Reports page. Extracted from the formerly-590-line page so the page can
 * focus on layout while the hook owns the data shape.
 */
export function useCrossAgencyReports() {
  const { isAzaiStaff, azaiRole } = useAgency();

  const [stats, setStats] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [sortBy, setSortBy] = useState('billed');
  const [sortOrder, setSortOrder] = useState('desc');

  const canViewReports = isAzaiStaff && ALLOWED_ROLES.includes(azaiRole);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, agenciesRes] = await Promise.all([
        api.get('/agencies/stats/cross-agency'),
        api.get('/agencies/'),
      ]);
      setStats(statsRes.data);
      setAgencies(agenciesRes.data.agencies || []);
    } catch {
      toast.error('Failed to load cross-agency data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAzaiStaff) loadData();
  }, [isAzaiStaff, timeRange, loadData]);

  // Sort + rank agencies according to the user's chosen sort key/order.
  // Memoized so the table doesn't re-sort on every unrelated re-render.
  const rankedAgencies = useMemo(() => {
    const list = stats?.by_agency || [];
    const sorted = [...list].sort((a, b) => {
      let aVal;
      let bVal;
      switch (sortBy) {
        case 'name': aVal = a.agency_name; bVal = b.agency_name; break;
        case 'patients': aVal = a.total_patients; bVal = b.total_patients; break;
        case 'claims': aVal = a.total_claims; bVal = b.total_claims; break;
        case 'collected': aVal = a.total_paid || 0; bVal = b.total_paid || 0; break;
        case 'rate':
          aVal = a.total_billed > 0 ? (a.total_paid / a.total_billed) : 0;
          bVal = b.total_billed > 0 ? (b.total_paid / b.total_billed) : 0;
          break;
        default: aVal = a.total_billed || 0; bVal = b.total_billed || 0;
      }
      if (sortBy === 'name') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted.map((agency, idx) => ({
      ...agency,
      rank: idx + 1,
      collectionRate: agency.total_billed > 0
        ? ((agency.total_paid / agency.total_billed) * 100).toFixed(1)
        : 0,
      denialRate: agency.total_claims > 0
        ? ((agency.claims_denied / agency.total_claims) * 100).toFixed(1)
        : 0,
    }));
  }, [stats, sortBy, sortOrder]);

  // Top-3 + needs-attention slices (also memoized).
  const slices = useMemo(() => ({
    topByBilled: [...rankedAgencies]
      .sort((a, b) => (b.total_billed || 0) - (a.total_billed || 0))
      .slice(0, 3),
    topByRate: [...rankedAgencies]
      .sort((a, b) => parseFloat(b.collectionRate) - parseFloat(a.collectionRate))
      .slice(0, 3),
    needsAttention: rankedAgencies.filter(
      (a) => parseFloat(a.denialRate) > 10 || parseFloat(a.collectionRate) < 70,
    ),
  }), [rankedAgencies]);

  // Totals across all agencies (raw $ values, not derived).
  const totals = useMemo(() => {
    const totalBilled = stats?.total_billed || 0;
    const totalCollected = rankedAgencies.reduce((sum, a) => sum + (a.total_paid || 0), 0);
    const overallRate = totalBilled > 0
      ? ((totalCollected / totalBilled) * 100).toFixed(1)
      : 0;
    return { totalBilled, totalCollected, overallRate };
  }, [stats, rankedAgencies]);

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/reports/cross-agency?format=${format}`, {
        responseType: format === 'json' ? 'json' : 'blob',
      });
      const blob = format === 'json'
        ? new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
        : new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cross-agency-report.${format}`;
      link.click();
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch {
      toast.error('Failed to export report');
    }
  };

  return {
    canViewReports, loading, stats, agencies,
    timeRange, setTimeRange,
    sortBy, setSortBy, sortOrder, setSortOrder,
    rankedAgencies, slices, totals,
    loadData, handleExport,
  };
}
